import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { LogOut, Package, User as UserIcon, Heart } from "lucide-react";
import { API, img, inrFmt } from "@/lib/format";
import { useStore } from "@/context/StoreContext";
import ProductCard from "@/components/ProductCard";
import { GoogleMark } from "@/components/GoogleMark";
import useSEO from "@/hooks/useSEO";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const signIn = () => {
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/account")}`;
};

export default function Account() {
  const { user, setUser, wishlist } = useStore();
  const [orders, setOrders] = useState(null);
  const [products, setProducts] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [tab, setTab] = useState("orders");
  useSEO({ title: "My Account — Just Kords", description: "Your Just Kords account — profile, order history, wishlist and tracking." });

  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/auth/wallet`, { withCredentials: true }).then((r) => setWallet(r.data)).catch(() => setWallet(null));
    axios.get(`${API}/auth/my-orders`, { withCredentials: true })
      .then((r) => setOrders(r.data))
      .catch(() => setOrders([]));
  }, [user]);

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(null);
  };

  if (user === undefined) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center bg-alabaster pt-28">
        <p className="font-serif text-3xl font-light text-mutedc">Loading your account…</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-[80vh] flex-col items-center justify-center gap-5 bg-alabaster px-4 pt-28 text-center" data-testid="account-signin-prompt">
        <span className="rounded-full bg-crimson-sub p-5 text-crimson"><UserIcon size={40} strokeWidth={1.4} /></span>
        <h1 className="font-serif text-5xl font-light">Your account</h1>
        <p className="max-w-sm text-sm leading-relaxed text-mutedc">
          Sign in with Google to see your order history, track shipments faster and check out in a tap.
        </p>
        <button data-testid="account-signin-button" onClick={signIn} className="mt-2 flex items-center gap-3 rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-ink shadow-md transition-all hover:shadow-lg">
          <GoogleMark /> Continue with Google
        </button>
      </div>
    );
  }

  return (
    <div className="bg-alabaster pt-28 lg:pt-36">
      <div className="mx-auto max-w-5xl px-4 pb-24 sm:px-6">
        <div className="flex flex-wrap items-center gap-6 border-b border-linec pb-10" data-testid="account-profile">
          {user.picture
            ? <img src={user.picture} alt={user.name} className="h-20 w-20 rounded-full border-2 border-crimson object-cover" />
            : <span className="flex h-20 w-20 items-center justify-center rounded-full bg-crimson text-3xl text-white">{(user.name || user.email)[0].toUpperCase()}</span>}
          <div className="flex-1">
            <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-crimson">My Account</p>
            <h1 className="mt-2 font-serif text-4xl font-light">{user.name || "Kords Club Member"}</h1>
            <p className="text-sm text-mutedc">{user.email}</p>
          </div>
          <button data-testid="account-logout" onClick={logout} className="flex items-center gap-2 rounded-full border border-ink px-5 py-2.5 text-sm font-semibold transition-colors hover:bg-ink hover:text-white">
            <LogOut size={15} /> Sign out
          </button>
        </div>

        {wallet && (
          <div className="mt-6 border border-crimson bg-crimson-sub p-6" data-testid="account-wallet">
            <div className="flex flex-wrap items-start justify-between gap-6">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-mutedc">Kords Wallet</p>
                <p className="mt-1 font-serif text-3xl text-crimson" data-testid="account-wallet-balance">{inrFmt(wallet.balance)}</p>
                <p className="text-xs text-mutedc">available cashback · 10% of every purchase comes back here</p>
                {wallet.expiring_soon > 0 && (
                  <p className="mt-1 text-xs font-semibold text-crimson" data-testid="account-wallet-expiring">{inrFmt(wallet.expiring_soon)} expiring within 30 days</p>
                )}
              </div>
              <Link to="/shop" data-testid="account-wallet-shop-cta" className="rounded-full bg-crimson px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-crimson-dark">Spend &amp; Earn</Link>
            </div>
            {wallet.txns.length > 0 && (
              <div className="mt-5 border-t border-crimson/20 pt-4">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-mutedc">History</p>
                <div className="mt-3 space-y-2" data-testid="account-wallet-history">
                  {wallet.txns.slice(0, 8).map((t, i) => (
                    <div key={i} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                      <span className="text-mutedc">
                        {t.type === "earned" ? "Cashback earned" : t.type === "redeemed" ? "Used on order" : t.type === "voided" ? "Cashback removed" : "Cashback expired"}
                        {t.order_id && <span className="font-mono text-[10px] uppercase"> · {t.order_id}</span>}
                      </span>
                      <span className="flex items-center gap-3">
                        {t.type === "earned" && t.status !== "used" && t.expires_at && (
                          <span className={`font-mono text-[9px] uppercase tracking-widest ${t.status === "expired" ? "text-mutedc" : "text-crimson"}`}>
                            {t.status === "expired" ? "expired" : `${inrFmt(t.remaining)} left · till ${new Date(t.expires_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}`}
                          </span>
                        )}
                        <span className={`font-semibold ${t.amount >= 0 ? "text-green-700" : "text-crimson"}`}>
                          {t.amount >= 0 ? "+" : "−"}{inrFmt(Math.abs(t.amount))}
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <p className="mt-4 text-[10px] leading-relaxed text-mutedc">
              Cashback is 10% of product value (excluding shipping), earned on every purchase while signed in. Valid 90 days from the date it's earned, non-transferable and can't be exchanged for cash.
            </p>
          </div>
        )}

        <div className="flex gap-2 pt-10">
          {[["orders", "Orders", Package], ["wishlist", "Wishlist", Heart]].map(([id, label, Icon]) => (
            <button key={id} data-testid={`account-tab-${id}`} onClick={() => setTab(id)}
              className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-200 ${tab === id ? "bg-crimson text-white" : "border border-linec bg-white text-mutedc hover:text-ink"}`}>
              <Icon size={15} /> {label}
              {id === "wishlist" && wishlist.length > 0 && ` (${wishlist.length})`}
            </button>
          ))}
        </div>

        {tab === "orders" && (
        <>
        <h2 className="pt-8 font-serif text-3xl font-light">Order history</h2>
        {orders === null ? (
          <p className="py-10 text-sm text-mutedc">Fetching your orders…</p>
        ) : orders.length === 0 ? (
          <div className="mt-6 flex flex-col items-start gap-4 border border-linec bg-white p-8" data-testid="account-orders-empty">
            <p className="text-sm text-mutedc">No orders yet — your next co-ord is waiting.</p>
            <Link to="/shop" className="rounded-full bg-crimson px-6 py-3 text-sm font-semibold text-white">Shop the Collection</Link>
          </div>
        ) : (
          <div className="mt-6 space-y-4" data-testid="account-orders">
            {orders.map((o) => (
              <div key={o.order_id} className="flex flex-wrap items-center gap-4 border border-linec bg-white p-5" data-testid={`account-order-${o.order_id}`}>
                <div className="flex gap-2">
                  {o.items.slice(0, 2).map((i) => (
                    <img key={`${i.slug}:${i.size}`} src={i.image ? img(i.image, 120) : ""} alt="" className="h-14 w-12 bg-linec object-cover" />
                  ))}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-mono text-sm font-medium text-crimson">{o.order_id}</p>
                  <p className="text-xs text-mutedc">
                    {new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })} · {o.items.length} item{o.items.length > 1 ? "s" : ""}
                  </p>
                </div>
                <span className={`px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest ${o.status === "Delivered" ? "bg-green-100 text-green-800" : o.status === "Cancelled" ? "bg-linec text-mutedc" : "bg-crimson-sub text-crimson"}`}>{o.status}</span>
                <p className="text-sm font-semibold">{inrFmt(o.total_inr)}</p>
                <Link to={`/track-order?id=${o.order_id}&c=${encodeURIComponent(o.phone)}`} data-testid={`account-track-${o.order_id}`} className="text-xs font-semibold uppercase tracking-widest text-crimson underline underline-offset-4">
                  Track
                </Link>
              </div>
            ))}
          </div>
        )}
        </>
        )}

        {tab === "wishlist" && (
          <div className="mt-8" data-testid="account-wishlist">
            {(() => {
              const wished = products.filter((p) => wishlist.includes(p.slug));
              if (wished.length === 0) {
                return (
                  <div className="flex flex-col items-start gap-4 border border-linec bg-white p-8" data-testid="account-wishlist-empty">
                    <p className="text-sm text-mutedc">Nothing hearted yet — tap the heart on any set to save it here.</p>
                    <Link to="/shop" data-testid="account-wishlist-shop-cta" className="rounded-full bg-crimson px-6 py-3 text-sm font-semibold text-white">Browse the Collection</Link>
                  </div>
                );
              }
              return (
                <div className="grid grid-cols-2 gap-x-6 gap-y-10 lg:grid-cols-3" data-testid="account-wishlist-grid">
                  {wished.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
}
